import { useEffect, useState } from "react";
import { ChevronLeft, Minus, Plus, Truck } from "lucide-react";
import { formatMoney } from "../../../lib/money";
import { useShop } from "../../../stores/cart";
import { Price, ProductCard, WishlistButton, isSoldOut } from "./ProductCard";
import { useStorefrontContext } from "./StorefrontContext";

export function ProductPage({ productSlug }: { productSlug: string }) {
  const { store, slug, paths, go, findProduct } = useStorefrontContext();
  const product = findProduct(productSlug);
  const add = useShop((s) => s.add);
  const openCart = useShop((s) => s.setDrawerOpen);

  const [imageIndex, setImageIndex] = useState(0);
  const [selected, setSelected] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setImageIndex(0);
    setSelected({});
    setQuantity(1);
    setError(null);
    if (product) document.title = `${product.name} · ${store.name}`;
  }, [productSlug, product, store.name]);

  if (!product) {
    return (
      <section className="zs-page zs-empty">
        <h1 className="zs-title">Product not found</h1>
        <p>It may have sold out or been removed.</p>
        <button type="button" className="zs-btn" onClick={() => go(paths.shop)}>
          Back to the shop
        </button>
      </section>
    );
  }

  const soldOut = isSoldOut(product);
  const maxQuantity = Math.min(99, product.stock ?? 99);
  const image = product.images[imageIndex] ?? product.images[0];
  const related = store.products
    .filter((p) => p.id !== product.id && p.collection === product.collection)
    .slice(0, 4);
  const freeOver = store.deliveryOptions.find((d) => !d.pickup && d.freeOverMinor)?.freeOverMinor;

  const addToCart = () => {
    const missing = product.options.find((opt) => !selected[opt.name]);
    if (missing) {
      setError(`Choose a ${missing.name.toLowerCase()}`);
      return;
    }
    add(slug, {
      productId: product.id,
      slug: product.slug,
      name: product.name,
      image: product.images[0]?.url,
      priceMinor: product.priceMinor,
      options: selected,
      quantity,
    });
    openCart(true);
  };

  return (
    <section className="zs-page">
      <button type="button" className="zs-back" onClick={() => go(paths.shop)}>
        <ChevronLeft size={16} aria-hidden="true" />
        Shop{product.collection ? ` / ${product.collection}` : ""}
      </button>

      <div className={`zs-product zs-product--${store.productLayout}`}>
        <div className="zs-gallery">
          {store.productLayout === "gallery" ? (
            // Gallery layout: every photo stacked, details stay in view.
            product.images.map((img, i) => (
              <div key={img.url} className="zs-gallery__main">
                <img src={img.url} alt={img.alt || `${product.name} photo ${i + 1}`} />
              </div>
            ))
          ) : (
            <div className="zs-gallery__main">
              {image ? <img src={image.url} alt={image.alt || product.name} /> : null}
            </div>
          )}
          {store.productLayout !== "gallery" && product.images.length > 1 ? (
            <div className="zs-gallery__thumbs" role="group" aria-label="Product photos">
              {product.images.map((img, i) => (
                <button
                  key={img.url}
                  type="button"
                  className="zs-thumb"
                  aria-pressed={i === imageIndex}
                  aria-label={`Show photo ${i + 1}`}
                  onClick={() => setImageIndex(i)}
                >
                  <img src={img.url} alt="" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="zs-product__info">
          {product.collection ? <span className="zs-eyebrow">{product.collection}</span> : null}
          <h1 className="zs-title">{product.name}</h1>
          <Price product={product} large />
          {product.stock !== null && product.stock > 0 && product.stock <= 5 ? (
            <p className="zs-stock-note">Only {product.stock} left</p>
          ) : null}

          {product.options.map((opt) => (
            <fieldset key={opt.name} className="zs-option">
              <legend>
                {opt.name}
                {selected[opt.name] ? <span className="zs-option__value">{selected[opt.name]}</span> : null}
              </legend>
              <div className="zs-option__values">
                {opt.values.map((value) => (
                  <button
                    key={value}
                    type="button"
                    className="zs-pill"
                    aria-pressed={selected[opt.name] === value}
                    onClick={() => {
                      setError(null);
                      setSelected((prev) => ({ ...prev, [opt.name]: value }));
                    }}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </fieldset>
          ))}

          <div className="zs-buy">
            <div className="zs-qty" aria-label="Quantity">
              <button
                type="button"
                aria-label="Decrease quantity"
                disabled={quantity <= 1}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                <Minus size={14} aria-hidden="true" />
              </button>
              <span aria-live="polite">{quantity}</span>
              <button
                type="button"
                aria-label="Increase quantity"
                disabled={quantity >= maxQuantity}
                onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
              >
                <Plus size={14} aria-hidden="true" />
              </button>
            </div>
            <button
              type="button"
              className="zs-btn zs-btn--block"
              disabled={soldOut}
              onClick={addToCart}
            >
              {soldOut ? "Sold out" : "Add to cart"}
            </button>
          </div>
          {error ? (
            <p className="zs-error" role="alert">
              {error}
            </p>
          ) : null}

          {freeOver ? (
            <p className="zs-perk">
              <Truck size={16} aria-hidden="true" />
              Free delivery on orders over {formatMoney(freeOver, store.currency)}
            </p>
          ) : null}

          {product.description ? <p className="zs-description">{product.description}</p> : null}
        </div>
      </div>

      {related.length > 0 ? (
        <div className="zs-related">
          <h2 className="zs-subtitle">You may also like</h2>
          <div className="zs-grid">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
